import { colors } from '@lmc/common/constants'
import React, { type ReactElement } from 'react'
import Text from '../Text'

type Props = {
  color?: string
  description?: string
  icon?: string
  removedLog?: boolean
  label?: string
  onPress?: () => void
  pictures?: string[]
  style?: object
  subtext?: string
  title?: string
}

export default function LogSummary({
  color = colors.darkblue,
  description,
  icon,
  removedLog,
  label,
  onPress,
  pictures = [],
  style,
  subtext,
  title
}: Props): ReactElement {
  const containerStyle = {
    ...styles.logContainer,
    ...(!!onPress && { cursor: 'pointer' }),
    ...style
  }

  const iconContainerStyle = {
    ...styles.iconContainer,
    backgroundColor: removedLog ? colors.grey : color
  }

  const titleStyle = {
    ...styles.title,
    ...(removedLog && { color: colors.darkgrey })
  }

  const pictureStyle = {
    ...styles.picture,
    ...(removedLog && { opacity: 0.2 })
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: the row renders its own <a> picture links, so it cannot itself be an anchor or button without nesting interactive content
    <div
      onClick={onPress}
      onKeyPress={onPress}
      role="link"
      style={containerStyle}
      tabIndex={0}
    >
      <div style={iconContainerStyle}>
        <img alt="" style={styles.icon} src={icon} />
      </div>
      <div>
        <Text style={styles.label} as="p1">
          {label}
        </Text>
        <Text style={titleStyle}>{title}</Text>
        <Text style={styles.description}>{description}</Text>
        {subtext && (
          <Text style={styles.subtext} as="p1">
            {subtext}
          </Text>
        )}
        {pictures &&
          pictures.map((url) => (
            <a
              className="lmc-img__link"
              href={url}
              key={url}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
            >
              <img alt="" src={url} style={pictureStyle} />
            </a>
          ))}
      </div>
    </div>
  )
}

const styles = {
  description: {
    color: colors.black,
    display: 'block',
    marginTop: 2,
    fontSize: 14,
    lineHeight: 1.4
  },
  icon: {
    width: 20
  },
  iconContainer: {
    alignItems: 'center',
    borderRadius: 46,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    height: 46,
    marginRight: 12,
    width: 46
  },
  label: {
    color: colors.darkgrey,
    display: 'block',
    fontSize: 12,
    lineHeight: 1.4
  },
  logContainer: {
    alignItems: 'center',
    display: 'flex',
    lineHeight: 1.4
  },
  picture: {
    height: 100,
    borderRadius: 10,
    marginTop: 8,
    marginRight: 8,
    background: colors.background,
    cursor: 'pointer'
  },
  subtext: {
    color: colors.darkgrey,
    display: 'block',
    marginTop: 5,
    fontSize: 14,
    lineHeight: 1.4
  },
  title: {
    color: colors.black,
    display: 'block',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.4
  }
}
